<template>
  <div class="row align-items-start no-gutters row-narrow"
       v-if="!forVisit || visit.is_paid != '1'">

    <div class="col-md-3 col-12">

      <div class="row no-gutters align-items-center">
        <div class="col-auto">
          <a class="pointer pr-3"
             v-if="canBeDeleted && pay_method !== 4"
             @click="deleteInput">
            <TrashSvg class="svg-stroke-red"/>
          </a>
        </div>
        <div class="col">
          <SelectGroup :options="payMethodsFiltered"
                       variant="white"
                       :label="$t('payment_form')"
                       :null-option="false"
                       :readonly="payMethodCannotBeChanged"
                       :key="`sel-${excludePayOptions.join('-')}`"
                       title="translatedTitle"
                       :translate="false"
                       :showlabel="true"
                       v-model="pay_method"/>
          <ValidationError validationId="form"/>
        </div>
      </div>
    </div>

    <div class="col-md-3 col-12"
         v-if="pay_method == 100">
      <SelectGroup :options="parentAccountDepositAccounts"
                   variant="white"
                   :label="$t('family_account')"
                   :translate="false"
                   title="label"
                   :showlabel="true"
                   v-model="parentAccountDepositAccountId">
        <div class="text-muted small">{{ $t('available') }}: {{ formatCurrency(parentAccountObject?.amount || 0) }}</div>
      </SelectGroup>
    </div>

    <div class="col-md-3 col-12"
         v-if="pay_method == 4 && insuranceLoaded">
      <SelectGroup :options="insuranceCompanies"
                   variant="white"
                   :label="$t('insurance_company')"
                   :translate="false"
                   :readonly="amountLockedByInsurance"
                   title="title"
                   :showlabel="true"
                   v-model="insurance_company_id"/>
    </div>

    <div class="col-md-3 col-12"
         v-if="pay_method == 4 && insuranceLoaded">
      <SelectGroup :options="polisesOfInsuranceCompany"
                   variant="white"
                   :label="$t('insurance_polis')"
                   :nullOption="!insurancePolis || !polisesOfInsuranceCompany.length"
                   :translate="false"
                   title="title"
                   :showlabel="true"
                   v-model="insurance_polis_id">
        <div class="under-form-group mt-1 small text-muted"
             v-if="insurance_polis_id">
          {{ $t('limit') }}: {{ formatCurrency(insuranceLimit) }}
        </div>
      </SelectGroup>
    </div>

    <div class="col-md-3 col-12"
         v-if="pay_method == 5 && deposit_accounts.length > 1">
      <SelectGroup :options="deposit_accounts"
                   variant="white"
                   :label="$t('deposit_account')"
                   :translate="false"
                   :null-option="false"
                   title="title"
                   :showlabel="true"
                   v-model="deposit_account_id">
        <div class="text-muted small">{{ $t('available') }}: {{ formatCurrency(depositAccountSelected?.amount || 0) }}</div>
      </SelectGroup>
    </div>

    <div class="col-md-3 col-12"
         v-if="pay_method != 7">
      <InputGroup type="text"
                  :renderResult="numberInputFormatterAmount"
                  :label="$t('sum')"
                  validationId="amountinput"
                  ref="amountinput"
                  :placeholder="leftToPayAfterTotalAmount > 0 ? $t('from_to', { from: 0, to: leftToPayAfterTotalAmount }) : $t('sum')"
                  :readonly="amountLockedByInsurance && !paymentsForInsuranceVisits"
                  :key="`amount-key-${amountKey}`"
                  :required="true"
                  v-model="amount">
        <div class="small text-muted"
             v-if="canBeWithdrawnFromDepositOrFamily > 0">
          <span>{{ $t('can_be_written_off') }}:</span>
          <span class="ml-1 text-underlined pointer text-black"
                @click="amount = canBeWithdrawnFromDepositOrFamily">
            {{ formatCurrency(canBeWithdrawnFromDepositOrFamily) }}
          </span>
        </div>
      </InputGroup>
      <ValidationError validationId="paid"/>
      <div class="under-form-group mt-1 d-block d-md-none"
           v-if="patient">
        <div>
          <small>{{ $t('deposit') }}: {{ formatCurrency(patient.advance_calculated) }}</small>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import SelectGroup from "@/components/form/SelectGroup.vue"
import InputGroup from "@/components/form/InputGroup.vue"
import ValidationError from "@/components/form/ValidationError.vue"
// import TrashSvg from '@/assets/svg-vue/general/trash.svg'
// import TrashSvg from '@/assets/svg-vue/general/delete.svg'
import TrashSvg from '@/assets/svg-vue/general/delete2.svg'
import {formatCurrency, formatNumber} from "@/extensions/filters/filters"
import {useStore} from "vuex"
import {computed, getCurrentInstance, onBeforeMount, onMounted, ref, watch} from "vue"
import {PatientService} from "@/services/api/patient.api.service"
import {copyObject} from "@/extensions/prototypes/prototypes"
import {numberInputFormatter} from "@/services/number.service"
import { EloquentService } from "@/services/api/eloquent.api.service"
import moment from "moment/moment"

const props = defineProps({
  canBeProcessedWithoutPayment: {
    type: Boolean,
    default: false
  },
  canBeDeleted: {
    type: Boolean,
    default: false
  },
  mainPayment: {
    type: Boolean,
    default: false
  },
  leftToPay: {
    type: Number,
    default: 0
  },
  leftToPayAfterTotalAmount: {
    type: Number,
    default: 0
  },
  totalCost: {
    type: Number,
    default: 0
  },
  value: {
    type: Object,
    default: () => ({})
  },
  excludePayOptions: {
    type: Array,
    default: () => []
  },
  paymentFor: {
    type: String,
    default: 'visit'
  }
})

const emit = defineEmits([
  'resetBonus',
  'paymentMethodUpdated',
  'changePPO',
  'paymentMethodDelete',
  'input',
  'removeOtherPayments'
])

const store = useStore()

let i18n = null

const amount = ref(null)
const deposit_account_id = ref(null)
// const parentAccount = ref(null)
const parentAccountDepositAccountId = ref(null)
const pay_method = ref(1)
const insuranceLoaded = ref(false)
const insurance_company_id = ref(null)
const insurance_polis_id = ref(null)
const amountinput = ref(null)
const payMethods = ref([])
const amountKey = ref(1)
const insuranceCompanies = ref([])
const insurancePolises = ref([])

const rights = computed(() => store.state.auth.rights)
const visit = computed(() => store.state.patient.visit)
const patient = computed(() => store.state.patient.patient)
const branchId = computed(() => store.state.auth.branch)
const branches = computed(() => store.state.auth.branches)
const pay_methods = computed(() => store.state.auth.pay_methods)
const insurancePolis = computed(() => insurancePolises.value.find(o => Number(o.id) === Number(insurance_polis_id.value)) || visit.value?.insurance_polis)
const amountLockedByInsurance = computed(() => !!insurancePolis.value || !!visit.value?.insurance_company_id)
const parentAccounts = computed(() => (patient.value?.parent_family_accounts || []).filter(o => o.pivot.active)) // deposit_by_accounts
const parentAccountDepositAccounts = computed(() => parentAccounts.value.map((o) => o.deposit_by_accounts.map((d) => ({
  id: d.id,
  amount: d.amount,
  parentAccountId: o.id,
  branchId: d.branch_id,
  label: `${o.full_name}, ${d.deposit_account?.title}: ${formatNumber(d.amount)}`
}))).flat().filter(o => o.branchId === branchId.value))
const kkmEnabled = computed(() => store.getters.kkmEnabled)
const deposit_accounts = computed(() => store.state.auth.deposit_accounts)
const polises = computed(() => patient.value?.insurance_polises || [])
const polisesOfInsuranceCompany = computed(() => {
  return polises.value.filter(polis => Number(polis.insurance_company_id) === Number(insurance_company_id.value) && polisIsValid(polis))
})
const paymentsForInsuranceVisits = computed(() => store.getters.getClinicAdditional('payments_for_insurance_visits'))
const payMethodsFiltered = computed(() => payMethods.value.filter(method => !props.excludePayOptions.includes(method.id)))
const insuranceLimit = computed(() => {
  if(!insurancePolis.value) return 0
  return insurancePolis.value.limit_available
})
const payMethodCannotBeChanged = computed(() => {
  const changeAdditionalToInsurancePayment = (Number(pay_method.value) !== 4 && paymentsForInsuranceVisits.value)
  return props.canBeProcessedWithoutPayment || (amountLockedByInsurance.value && !changeAdditionalToInsurancePayment)
})
const forVisit = computed(() => props.paymentFor === 'visit')
const parentAccountObject = computed(() => parentAccountDepositAccounts.value.find(o => Number(o.id) === Number(parentAccountDepositAccountId.value)))
const parentAccount = computed(() => parentAccountObject.value?.parentAccountId || null)
const depositAccountSelected = computed(() => {
  if(!deposit_account_id.value) return null
  const currentBranchData = (branches.value || []).find(b => b.id === branchId.value)
  if(!currentBranchData) return null
  return (currentBranchData?.deposits || []).find(d => d.deposit_account_id === Number(deposit_account_id.value))
})
const canBeWithdrawnFromDepositOrFamily = computed(() => {
  let amount = 0
  if(depositAccountSelected.value) amount = depositAccountSelected.value.amount
  if(parentAccountObject.value) amount = parentAccountObject.value.amount
  return Math.min(amount, props.leftToPayAfterTotalAmount)
})

const autofillInsurance = async () => {
  if(!props.mainPayment) return
  if(insurancePolis.value || visit.value?.insurance_company_id) {
    insurance_company_id.value = insurancePolis.value?.insurance_company_id || visit.value.insurance_company_id
    insurance_polis_id.value = insurancePolis.value?.id || visit.value.insurance_polis_id
    pay_method.value = 4
    amount.value = paymentsForInsuranceVisits.value
        ? Math.min(props.leftToPay, Number(insurancePolis.value?.limit_available || 0))
        : props.leftToPay
    if(insurancePolis.value && !polisIsValid(insurancePolis.value) && !visit.value.is_paid) { // means it was selected initially, but now it's not valid
      insurance_polis_id.value = null
      await saveInsurancePolisToRecord(null)
      emitChange()
    }
  }
}

const numberInputFormatterAmount = (val) => {
  val = numberInputFormatter(val)
  if(val === amount.value) {
    amountKey.value++
    setTimeout(() => amountinput.value.focus(), 10)
  }
  return val
}

const polisIsValid = (polis) => {
  let dateToCompare = moment().format('YYYY-MM-DD')
  if(visit.value?.start) {
    dateToCompare = moment(visit.value?.start).format('YYYY-MM-DD')
  }
  return polis.valid_from <= dateToCompare && polis.valid_to >= dateToCompare
}

const emitChange = () => {
  const dataToPass = {
    amount: amount.value,
    pay_method: pay_method.value,
    parentAccountObject: copyObject(parentAccountObject.value),
    parentAccount: parentAccount.value,
    parentAccountDepositAccountId: parentAccountDepositAccountId.value,
    deposit_account_id: deposit_account_id.value,
    insurance_company_id: insurance_company_id.value,
    insurance_polis_id: insurance_polis_id.value,
    // needsToSelectPolisBeforeUsingInsurance: Number(pay_method.value) === 4 && !visit.value?.insurance_polis_id && polisesOfInsuranceCompany.value.length
    needsToSelectPolisBeforeUsingInsurance: Number(pay_method.value) === 4 && !visit.value?.insurance_polis_id && !insurance_polis_id.value
  }
  emit('input', dataToPass)
}

const deleteInput = () => {
  emit('paymentMethodDelete')
}

defineExpose({
  autofillInsurance
})

watch(pay_method, (val) => {
  const payMethodObject = payMethods.value.find(x => Number(x.id) === Number(val))
  if(Number(val) !== 100) {
    parentAccountDepositAccountId.value = null
    // parentAccount.value = null
  }
  if(Number(val) === 4) { // insurance
    if(!insuranceLoaded.value) {
      const day = visit.value?.just_day || moment().format('YYYY-MM-DD')
      PatientService.getInsuranceData(patient.value.id, day).then(res => {
        insuranceCompanies.value = res.data.insurance_companies
        insurancePolises.value = res.data.insurance_polises
        insuranceLoaded.value = true
      })
    }
  } else {
    insurance_company_id.value = null
  }
  if(Number(val) === 7) {
    emit('changePPO', false)
  }
  if(kkmEnabled.value && (store.getters.dynamicConfigValue('kkm_acquiring_methods', branchId.value) || []).includes(Number(val))) {
    emit('changePPO', true)
  }
  if(payMethodObject?.custom_data_kkm_fiscal) {
    emit('changePPO', true)
  }
  if(Number(val) === 0) {
    emit('resetBonus')
  }
  if(Number(val) === 5) {
    if(deposit_accounts.value.length) {
      deposit_account_id.value = deposit_accounts.value.find(d => d.is_default === 1)?.id || deposit_accounts.value[0].id
    }
  } else {
    deposit_account_id.value = null
  }
})

watch(patient, (val) => {
  if(val) {
    if(parentAccounts.value
        && parentAccounts.value.length > 0
        && rights.value.includes('family_account')) {
      if(!payMethods.value.filter(x => x.id == 100).length) {
        payMethods.value.push({
          id: 100,
          title: 'family_account',
          translatedTitle: i18n.t('family_account')
        })
      }
    } else {
      payMethods.value = payMethods.value.filter(x => x.id != 100)
    }
  }
})

watch(() => props.canBeProcessedWithoutPayment, () => {
  watchProcessedWithoutPayment()
})

watch(() => props.totalCost, () => {
  autofillInsurance()
})

const watchProcessedWithoutPayment = () => {
  if(props.canBeProcessedWithoutPayment) {
    payMethods.value.push({
      id: 7,
      title: 'without_payment',
      translatedTitle: i18n.t('without_payment')
    })
    pay_method.value = 7
  } else {
    payMethods.value = payMethods.value.filter(x => Number(x.id) !== 7)
    if(Number(pay_method.value) === 7) pay_method.value = 1
  }
}

const saveInsurancePolisToRecord = async (insurancePolisId) => {
  try {
    let formData = new FormData()
    formData.append('insurance_polis_id', insurancePolisId || '')
    const res = await EloquentService.update('record', visit.value.id, formData)
    if(res.data.id) {
      store.commit('setVisit', {
        ...copyObject(visit.value),
        insurance_polis_id: insurancePolisId
      })
    }
    amount.value = Math.min(props.leftToPay, Number(insuranceLimit.value))
    emit('removeOtherPayments')
  } catch (error) {
    console.log(error)
  }
}

watch(insurance_polis_id, (val) => {
  if(val) {
    saveInsurancePolisToRecord(val)
  }
})

watch([
  amount,
  pay_method,
  parentAccountDepositAccountId,
  insurance_company_id,
  deposit_account_id,
  insurance_polis_id
], () => {
  emitChange()
})

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
})

onMounted(() => {
  payMethods.value = copyObject(pay_methods.value)
  if(!rights.value.includes('deposit')) {
    payMethods.value = payMethods.value.filter((obj) => obj.id !== 5)
  }
  if(props.paymentFor === 'material') {
    payMethods.value = payMethods.value.filter((obj) => obj.id !== 0 && obj.id !== 4)
  }
  payMethods.value = payMethods.value.filter((obj) => obj.id !== 6)
  if(props.value.amount) amount.value = props.value.amount
  if(props.value.pay_method) {
    pay_method.value = props.value.pay_method
  } else {
    pay_method.value = props.excludePayOptions.includes(1) ? (payMethodsFiltered.value[0].id || 1) : 1
  }
  // if(props.value.parentAccount) parentAccount.value = props.value.parentAccount
  if(props.value.parentAccountDepositAccountId) parentAccountDepositAccountId.value = props.value.parentAccountDepositAccountId
  if(props.value.deposit_account_id) deposit_account_id.value = props.value.deposit_account_id
  if(props.value.insurance_company_id) insurance_company_id.value = props.value.insurance_company_id
  if(props.value.insurance_polis_id) insurance_polis_id.value = props.value.insurance_polis_id
  watchProcessedWithoutPayment()
  autofillInsurance()
  emitChange()
})
</script>
